import { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Show, SignIn, SignUp, useAuth, useClerk } from "@clerk/react";
import { Toaster } from "sonner";
import { Loader2 } from "lucide-react";
import ClerkAuthProvider from "@/components/auth/ClerkAuthProvider";
import Studio from "@/components/studio/Studio";
import UsersPage from "@/components/studio/UsersPage";
import { fetchMe, setTokenGetter } from "@/lib/api";
import PublicCatalog from "@/components/public/PublicCatalog";
import BlackFriday from "@/components/bf/BlackFriday";
import BFStudio from "@/components/bf/BFStudio";
import BFBillingPage from "@/components/bf/BFBillingPage";
import BFConsent from "@/components/bf/BFConsent";
import BFContractSign from "@/components/bf/BFContractSign";
import BFClientPage from "@/components/bf/BFClientPage";

const AuthPage = ({ children }) => (
  <div className="auth-page" data-testid="auth-page">
    <div className="auth-brand">
      <span className="st-brand-mark">Beauty <em>Trends</em></span>
      <span className="st-brand-sub">Acces agenți · Black Friday 2026</span>
    </div>
    {children}
  </div>
);

const TicketSwitch = ({ children }) => {
  const { isLoaded, isSignedIn } = useAuth();
  const clerk = useClerk();
  const ticket = new URLSearchParams(window.location.search).get("__clerk_ticket");
  useEffect(() => {
    if (isLoaded && isSignedIn && ticket) clerk.signOut({ redirectUrl: window.location.href });
  }, [isLoaded, isSignedIn, ticket, clerk]);
  return ticket && (!isLoaded || isSignedIn) ? <Loading /> : children;
};

const Loading = () => (
  <div className="st-loading" data-testid="app-loading"><Loader2 className="animate-spin" /> Se încarcă…</div>
);

function Protected({ adminOnly, render }) {
  const { getToken } = useAuth();
  const [me, setMe] = useState(null);
  useEffect(() => {
    setTokenGetter(() => getToken());
    fetchMe().then(setMe).catch(() => setMe(false));
  }, [getToken]);
  if (me === null) return <Loading />;
  if (me === false) return <div className="st-loading">Nu am putut verifica contul. Reîncarcă pagina.</div>;
  if (adminOnly && me.role !== "admin") return <Navigate to="/" replace />;
  return render(me);
}

const Guard = (props) => (
  <>
    <Show when="signed-in"><Protected {...props} /></Show>
    <Show when="signed-out"><Navigate to="/sign-in" replace /></Show>
  </>
);

const BF_HOST = window.location.hostname.startsWith("blackfriday.");

function App() {
  if (BF_HOST) return (
    <BrowserRouter>
      <Routes><Route path="/acord/:t" element={<BFConsent />} /><Route path="/semnare/:t" element={<BFContractSign />} /><Route path="*" element={<BlackFriday />} /></Routes>
    </BrowserRouter>
  );
  return (
    <BrowserRouter>
      <Toaster theme="dark" position="top-center" />
      <ClerkAuthProvider>
        <TicketSwitch>
        <Routes>
          <Route path="/sign-in/*" element={<AuthPage><SignIn routing="path" path="/sign-in" signUpUrl="/sign-up" withSignUp /></AuthPage>} />
          <Route path="/sign-up/*" element={<AuthPage><SignUp routing="path" path="/sign-up" signInUrl="/sign-in" /></AuthPage>} />
          <Route path="/c/:slug" element={<PublicCatalog />} />
          <Route path="/blackfriday" element={new URLSearchParams(window.location.search).get("edit") ? <Guard render={() => <BlackFriday />} /> : <BlackFriday />} />
          <Route path="/blackfriday/acord/:t" element={<BFConsent />} />
          <Route path="/semnare/:t" element={<BFContractSign />} />
          <Route path="/black-friday-admin" element={<Guard adminOnly render={() => <BFStudio />} />} />
          <Route path="/facturare-admin" element={<Guard adminOnly render={() => <BFBillingPage />} />} />
          <Route path="/client/:id" element={<Guard render={() => <BFClientPage />} />} />
          <Route path="/utilizatori" element={<Guard adminOnly render={(me) => <UsersPage me={me} />} />} />
          <Route path="/" element={<Guard render={(me) => <Studio me={me} />} />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </TicketSwitch>
      </ClerkAuthProvider>
    </BrowserRouter>
  );
}

export default App;
